import { FC } from 'react';
import { useToast, Flex, Button, Icon, Tag, TagLabel } from '@chakra-ui/react';
import { IList } from 'core/API/listApi';
import { useTranslation } from 'helpers/languages/translations';
import { tableToolbarStyle } from 'shared/styles';
import icons from 'shared/icons';
import { CellValue, SourceData } from 'pages/ListManagement/types';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { downloadExcelFile } from 'utils/files';
import MFileLinkUpload from './MFileLinkUpload/MFileLinkUpload';
import { getDataFromFile } from './ListTemplates/helpers/utils';
import { translateHeaders } from './ListTemplates/helpers/HeadersList';

export const MDataToolBar: FC<{
    list: IList;
    toolId: number;
    isSaved?: boolean;
    isDirty?: boolean;
    isTableValid?: boolean;
    // TODO add typings
    hotRef?: any;
    setIsDirty?: any;
    setIsTableValid?: any;
    setData?: any;
    headers: string[];
    beforeDownload?: (list: CellValue[][]) => CellValue[][];
    beforeUpload?: (rowsArray: SourceData) => SourceData;
    saveClickCallback: () => Promise<void>;
    disableSave?: boolean;
    readonly?: boolean;
}> = ({
    list,
    isDirty,
    setIsDirty,
    isTableValid,
    setIsTableValid,
    setData,
    headers,
    beforeDownload,
    beforeUpload,
    hotRef,
    saveClickCallback,
    disableSave,
    readonly,
}) => {
    const toast = useToast();
    const translate = useTranslation();
    const DownloadIcon = icons.download;
    const translatedHeaders = translateHeaders(headers);

    const handleChangeFile = async (file: File | null) => {
        setIsDirty(true);
        let rowsArray = await getDataFromFile(file);

        if (beforeUpload) {
            rowsArray = beforeUpload(rowsArray);
        }

        const hot = hotRef?.current?.hotInstance;
        hot?.updateData(rowsArray);
        if (setData) setData(rowsArray);
        hot?.validateCells((valid: boolean) => {
            setIsTableValid(valid);
            hot.render();
        });
    };

    const handleChangeFileToolBar = async (file: File | null) => {
        setIsDirty(true);
        await handleChangeFile(file);
    };

    const handleDownloadListData = () => {
        try {
            if (list) {
                const hot = hotRef?.current?.hotInstance;
                const tableData = [...(hot?.getData() as SourceData)];
                let data: CellValue[][] = tableData;
                if (beforeDownload) {
                    data = beforeDownload(tableData);
                }
                data = [translatedHeaders, ...data] as string[][];
                downloadExcelFile(list.name, 'Users', data as string[][]);
            }
        } catch {
            toast({
                title: "Can't download error file",
                status: 'error',
                isClosable: true,
            });
        }
    };

    return (
        <Flex padding={0} {...tableToolbarStyle}>
            {list.data?.length && (
                <Button
                    mr={4}
                    size="sm"
                    rightIcon={<Icon as={DownloadIcon} />}
                    onClick={() => handleDownloadListData()}
                >
                    Download List
                </Button>
            )}
            <MFileLinkUpload
                handleChangeFile={intentionalyFloatingPromiseReturn(handleChangeFileToolBar)}
                isDisabled={readonly}
            />
            {!readonly && (
                <Tag size="lg" colorScheme={!isDirty ? 'green' : 'orange'} ml={4} borderRadius="full">
                    <TagLabel>{!isDirty ? translate('saved') : translate('not.saved')}</TagLabel>
                </Tag>
            )}
            <Button
                ml={4}
                isDisabled={disableSave || readonly}
                colorScheme="blue"
                bg="blueMain"
                size="sm"
                onClick={intentionalyFloatingPromiseReturn(saveClickCallback)}
            >
                {translate('list.button.save')}
            </Button>
        </Flex>
    );
};
